'use client';

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Script from 'next/script';
import { COLORS } from '@/constants/colors';
import { SPACING } from '@/constants/spacing';
import { TYPOGRAPHY } from '@/constants/typography';
import { FormField as Field, Label, Input, TextArea, FieldError, SubmitButton, StatusMessage } from '@/components/ui/FormField';
import { getCmsComments, postCmsComment, type CmsApprovedComment } from '@/lib/cms';

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      render: (container: HTMLElement, params: Record<string, unknown>) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
    };
  }
}

export default function CommentSection({ postId }: { postId: number }) {
  const [comments, setComments] = useState<CmsApprovedComment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCmsComments(postId)
      .then((docs) => {
        if (!cancelled) setComments(docs);
      })
      .catch((err) => console.error('Failed to load comments:', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postId]);

  const handleSubmitted = (comment: { authorName: string; commentBody: string }) => {
    // Optimistic UX note: the new comment is pending moderation, so it is
    // intentionally NOT added to the visible list here - only the success
    // message confirms submission. It will appear once an admin approves it.
    void comment;
  };

  return (
    <Section id="comments">
      <Container>
        <Heading>Comments {comments.length > 0 && `(${comments.length})`}</Heading>

        {loading ? (
          <Muted>Loading comments...</Muted>
        ) : comments.length === 0 ? (
          <Muted>Be the first to leave a comment.</Muted>
        ) : (
          <CommentList>
            {comments.map((c) => (
              <CommentItem key={c.id}>
                <CommentAuthor>{c.authorName}</CommentAuthor>
                <CommentDate>
                  {new Date(c.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </CommentDate>
                <CommentBody>{c.commentBody}</CommentBody>
              </CommentItem>
            ))}
          </CommentList>
        )}

        <FormHeading>Leave a Comment</FormHeading>
        <CommentForm postId={postId} onSubmitted={handleSubmitted} />
      </Container>
    </Section>
  );
}

function CommentForm({
  postId,
  onSubmitted,
}: {
  postId: number;
  onSubmitted: (comment: { authorName: string; commentBody: string }) => void;
}) {
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | undefined>(undefined);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    // The Script tag's onLoad only means the api.js file finished
    // downloading/executing - grecaptcha's own internal setup (which
    // defines .render) can still be pending after that. grecaptcha.ready()
    // is Google's documented way to wait for the real "usable" point;
    // calling .render() straight from onLoad can throw
    // "grecaptcha.render is not a function".
    if (!recaptchaReady || !RECAPTCHA_SITE_KEY || !window.grecaptcha) return;

    window.grecaptcha.ready(() => {
      if (!recaptchaContainerRef.current || !window.grecaptcha || widgetIdRef.current !== undefined) return;
      widgetIdRef.current = window.grecaptcha.render(recaptchaContainerRef.current, { sitekey: RECAPTCHA_SITE_KEY });
    });
  }, [recaptchaReady]);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!authorName.trim()) next.authorName = 'Please enter your name.';
    if (!authorEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authorEmail.trim())) next.authorEmail = 'Enter a valid email address.';
    if (!commentBody.trim()) next.commentBody = 'Please write a comment.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // getResponse() throws "No reCAPTCHA clients exist" if no widget has
    // been rendered yet (e.g. the ready/render effect below hasn't run) -
    // guard on widgetIdRef so that case is treated as "not completed" rather
    // than crashing the submit handler.
    const recaptchaToken =
      RECAPTCHA_SITE_KEY && window.grecaptcha && widgetIdRef.current !== undefined
        ? window.grecaptcha.getResponse(widgetIdRef.current)
        : undefined;
    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setStatus('error');
      setStatusMessage('Please complete the "I\'m not a robot" check.');
      return;
    }

    setStatus('submitting');
    try {
      const data = await postCmsComment({
        post: postId,
        authorName: authorName.trim(),
        authorEmail: authorEmail.trim(),
        commentBody: commentBody.trim(),
        recaptchaToken,
      });

      if (!data.success) {
        setStatus('error');
        setStatusMessage(data.message || 'Something went wrong. Please try again.');
        if (window.grecaptcha) window.grecaptcha.reset(widgetIdRef.current);
        return;
      }

      setStatus('success');
      setStatusMessage(data.message || 'Comment submitted for review - it will appear once approved.');
      onSubmitted({ authorName: authorName.trim(), commentBody: commentBody.trim() });
      setAuthorName('');
      setAuthorEmail('');
      setCommentBody('');
      if (window.grecaptcha) window.grecaptcha.reset(widgetIdRef.current);
    } catch (err) {
      console.error('Failed to submit comment:', err);
      setStatus('error');
      setStatusMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <>
      {RECAPTCHA_SITE_KEY && (
        // onLoad only fires the first time this script loads anywhere on the
        // site - navigating client-side from one blog post to another won't
        // re-fire it, leaving this page's widget never rendered. onReady is
        // next/script's mechanism for that case: it fires on every mount,
        // including when the script was already loaded by a previous page.
        <Script
          src="https://www.google.com/recaptcha/api.js"
          strategy="afterInteractive"
          onLoad={() => setRecaptchaReady(true)}
          onReady={() => setRecaptchaReady(true)}
        />
      )}

      <form onSubmit={handleSubmit}>
        {status === 'success' && <StatusMessage $variant="success">{statusMessage}</StatusMessage>}
        {status === 'error' && <StatusMessage $variant="error">{statusMessage}</StatusMessage>}

        <Field>
          <Label htmlFor="comment-name">Name *</Label>
          <Input id="comment-name" type="text" value={authorName} onChange={(e) => setAuthorName(e.target.value)} $hasError={!!errors.authorName} />
          {errors.authorName && <FieldError>{errors.authorName}</FieldError>}
        </Field>

        <Field>
          <Label htmlFor="comment-email">Email * (not shown publicly)</Label>
          <Input id="comment-email" type="email" value={authorEmail} onChange={(e) => setAuthorEmail(e.target.value)} $hasError={!!errors.authorEmail} />
          {errors.authorEmail && <FieldError>{errors.authorEmail}</FieldError>}
        </Field>

        <Field>
          <Label htmlFor="comment-body">Comment *</Label>
          <TextArea id="comment-body" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} $hasError={!!errors.commentBody} />
          {errors.commentBody && <FieldError>{errors.commentBody}</FieldError>}
        </Field>

        {RECAPTCHA_SITE_KEY ? (
          <RecaptchaWrapper ref={recaptchaContainerRef} />
        ) : (
          <RecaptchaNotice>Comment verification isn&apos;t configured yet - your comment will still be reviewed before it appears.</RecaptchaNotice>
        )}

        <SubmitButton type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Submitting...' : 'Post Comment'}
        </SubmitButton>
      </form>
    </>
  );
}

const Section = styled.section`
  background: ${COLORS.bgGray};
  padding: 64px 24px;

  @media (max-width: 767px) {
    padding: 48px 20px;
  }
`;

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  ${TYPOGRAPHY.h2}
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.lg};
`;

const FormHeading = styled.h3`
  ${TYPOGRAPHY.h4}
  color: ${COLORS.textPrimary};
  margin: ${SPACING.xxl} 0 ${SPACING.md};
`;

const Muted = styled.p`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textMuted};
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
`;

const CommentItem = styled.div`
  background: ${COLORS.white};
  border-radius: 10px;
  padding: ${SPACING.md} ${SPACING.lg};
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
`;

const CommentAuthor = styled.span`
  ${TYPOGRAPHY.bodySM}
  font-weight: 700;
  color: ${COLORS.textPrimary};
  margin-right: ${SPACING.sm};
`;

const CommentDate = styled.span`
  ${TYPOGRAPHY.bodyXS}
  color: ${COLORS.textMuted};
`;

const CommentBody = styled.p`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  margin: ${SPACING.xs} 0 0;
  white-space: pre-wrap;
`;

const RecaptchaWrapper = styled.div`
  margin-bottom: ${SPACING.md};
`;

const RecaptchaNotice = styled.p`
  ${TYPOGRAPHY.bodyXS}
  color: ${COLORS.textMuted};
  margin: 0 0 ${SPACING.md};
`;
